/*
 * 模块: PeriodSelector 主组件
 * 职责: 周期选择器主入口，协调子组件和逻辑模块
 * 依赖: React, Ant Design, 子组件, Hooks
 * 导出: PeriodSelector 组件
 * 限制: 文件<280行, 函数<30行, 职责单一
 */

import React, { useState, useRef } from 'react';
import {
  Card,
  Button,
  message
} from 'antd';
import {
  UploadOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { periodAPI } from '../../services/api';
import type { PeriodSelectorProps } from './types';
import { PeriodOverview } from './components/PeriodOverview';
import { useBatchUpload } from './hooks/useBatchUpload';
import './PeriodSelector.css';

/**
 * 周期选择器组件
 * 卡片式时间轴UI + 先选周期再上传文件的流程
 */
export const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  periods,
  selectedPeriodId,
  skuId,
  onPeriodSelect,
  onPeriodUploaded
}) => {
  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 使用批量上传Hook
  const { batchUploading, uploadProgress, handleBatchUpload } = useBatchUpload({
    skuId,
    periods,
    onUploadComplete: onPeriodUploaded
  });

  /**
   * 处理周期删除
   */
  const handleDelete = async (periodId: number) => {
    try {
      await periodAPI.delete(periodId);
      message.success('删除成功');
      onPeriodUploaded();
    } catch (error) {
      message.error('删除失败');
    }
  };

  /**
   * 处理文件选择
   */
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    handleBatchUpload(fileArray);

    // 重置input以允许重复选择相同文件
    event.target.value = '';
  };

  /**
   * 触发文件选择
   */
  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      {/* 周期概览面板 */}
      <PeriodOverview
        periods={periods}
        selectedPeriodId={selectedPeriodId}
        selectedYear={selectedYear}
        onYearChange={setSelectedYear}
        onPeriodSelect={onPeriodSelect}
        onUploadClick={triggerFileSelect}
        onDelete={handleDelete}
      />

      {/* 批量上传按钮区域 */}
      <div className="period-timeline">
        <div className="period-cards-container">
          <Card className="add-period-card">
            {/* 隐藏的文件输入 */}
            <input
              type="file"
              ref={fileInputRef}
              multiple
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            {/* 上传按钮 */}
            <Button
              type="dashed"
              icon={batchUploading ? <LoadingOutlined /> : <UploadOutlined />}
              style={{ width: '100%', height: '100%' }}
              loading={batchUploading}
              onClick={triggerFileSelect}
            >
              {batchUploading ? '上传中...' : '批量上传周期'}
            </Button>
            {/* 上传进度显示 */}
            {Object.keys(uploadProgress).length > 0 && (
              <div style={{ marginTop: 8, fontSize: 11 }}>
                {Object.entries(uploadProgress)
                  .slice(0, 3)
                  .map(([file, status]) => (
                    <div
                      key={file}
                      style={{
                        color: '#8c8c8c',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {file}: {status}
                    </div>
                  ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
};

export default PeriodSelector;

